import { DOMAttributes, DOMElement } from 'react';

import { AxiosError } from 'axios';
import { TAxiosErrorDetails } from '@/types/common.ts';
import { LogRecordPrompt } from '@/types/logs';
import { TCurrency, TCurrencySign } from '@/types/settings';
import { renderHtmlContent } from '@/lib/utils/index';

export const getCurrencySign = (currency: TCurrency): TCurrencySign => {
  switch (currency.toLowerCase()) {
    case 'usd':
      return '$';
    case 'gbp':
      return '£';
    case 'eur':
      return '€';
    default:
      return '$';
  }
};

export const mockedResponse = async <A>(
  data: A,
  delay?: number
): Promise<A> => {
  await new Promise((resolve) => setTimeout(resolve, delay || 1000));
  return Promise.resolve(data);
};

export const dateFromTimestamp = (date: number) => {
  if (!date) return '';
  // Convert the date to ISO string
  const isoString = new Date(date).toISOString();
  // Split at the "T" character to get the date part
  const formattedDate = isoString.split('T')[0];
  return formattedDate;
};

export function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export const formatDate = (dateStr: string): { date: string; time: string } => {
  const date = new Date(dateStr);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    // If the date is invalid, return the original string or handle as needed
    console.warn(`Invalid date: ${dateStr}`);
    return { date: dateStr, time: '' };
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return {
    date: `${day}/${month}/${year}`,
    time: `${hours}:${minutes}:${seconds}`,
  };
};

export const formatCost = (d: number) => {
  return Math.ceil(d * 10000) / 10000;
};

export const truncateString = (str: string | null, maxLength = 20) => {
  str = !str ? '' : str;
  return str.length > maxLength ? str.slice(0, maxLength) + '...' : str;
};

export const parsePromptLastContent = (logPrompt: LogRecordPrompt[]) => {
  try {
    return logPrompt?.pop()?.message || '';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_e) {
    return JSON.stringify(logPrompt);
  }
};

export const parseLogString = (
  logPrompt: LogRecordPrompt[]
): string | (DOMElement<DOMAttributes<Element>, Element> | null)[] => {
  try {
    const preHtml = logPrompt.reduce((acc, str) => {
      if (str?.role) {
        acc += `<h4>${str.role}</h4>`;
      }
      if (str?.message) {
        acc += `<p>${str.message}</p>`;
      }

      return acc;
    }, '');

    return renderHtmlContent(preHtml) as (DOMElement<
      DOMAttributes<Element>,
      Element
    > | null)[];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_e) {
    return JSON.stringify(logPrompt);
  }
};

export function copyToClip(str: string) {
  function listener(e: ClipboardEvent) {
    if (e.clipboardData) {
      e.clipboardData.setData('text/html', str);
      e.clipboardData.setData('text/plain', str);
      e.preventDefault();
    }
  }

  document.addEventListener('copy', listener);
  document.execCommand('copy');
  document.removeEventListener('copy', listener);
}

export function uuidToColor(uuid: string) {
  // Hash the UUID into a numeric value
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    const char = uuid.charCodeAt(i);
    hash = (hash * 31 + char) & 0xffffffff; // Keep it 32-bit
  }

  // Convert the hash to a hex color code
  const r = (hash >> 16) & 0xff;
  const g = (hash >> 8) & 0xff;
  const b = hash & 0xff;

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export const parseError = (error: unknown) => {
  // @ts-ignore
  let errorCode = error?.error_code || '';
  // @ts-ignore
  let errorMessage = error?.message || error?.error_message || '';

  if (error instanceof AxiosError) {
    if (error.response?.data?.error_message) {
      errorMessage = error.response?.data?.error_message;
    }

    if (!errorCode && error.response?.data?.error_code) {
      errorCode = error.response.data.error_code;
    }

    if (error.response?.data?.error_details?.length) {
      const errorDetails = (
        error.response.data.error_details as TAxiosErrorDetails
      ).reduce((acc, err, i) => {
        acc.push(`Error #${i + 1}: ${err.msg}`);
        return acc;
      }, [] as string[]);

      if (errorDetails.length) {
        errorMessage += '\n' + errorDetails.join('\n');
      }
    }

    return { errorMessage, errorCode };
  }

  return {
    errorMessage,
    errorCode,
  };
};
